import React, { useState, useEffect } from 'react';
import { X, Eye, EyeOff } from 'lucide-react';
import { loginadmin } from '../../assets/assets';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useRequestAdminActivation, useVerifyAdminActivationCode, useCompleteAdminActivation } from '../../services/auth/auth';
import { useAlert } from '../../context/AlertContext';

const ActivateAdmin = ({ onClose }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const setAlert = useAlert();
  const currentStep = searchParams.get('step') || 'email';

  const { mutateAsync: requestActivation } = useRequestAdminActivation();
  const { mutateAsync: verifyCode } = useVerifyAdminActivationCode();
  const { mutateAsync: completeActivation } = useCompleteAdminActivation();
  
  const [formData, setFormData] = useState({
    email: '',
    verificationCode: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: ''
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(''); // Clear error when user types
  };
  
  const togglePasswordVisibility = (field) => {
    if (field === 'password') {
      setShowPassword(prev => !prev);
    } else {
      setShowConfirmPassword(prev => !prev);
    }
  };
  
  const goToStep = (step) => {
    setSearchParams({ step });
  };
  
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  
  const validatePassword = (password) => {
    const passwordRegex = /^(?=(?:[^A-Z]*[A-Z]){3})(?=(?:[^a-z]*[a-z]){2})(?=(?:\D*\d){2})(?=.*[^a-zA-Z0-9]).{8,}$/;
    return passwordRegex.test(password);
  };
  
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    setLoading(true);
    try {
      await requestActivation({ email: formData.email });
      setAlert({ type: 'success', message: 'Verification code sent to your email' });
      goToStep('verify');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to request activation');
    } finally {
      setLoading(false);
    }
  };
  
  const handleVerificationSubmit = async (e) => {
    e.preventDefault();
    if (formData.verificationCode.length !== 6) {
      setError('Please enter the 6-letter verification code');
      return;
    }
    
    setLoading(true);
    try {
      await verifyCode({ email: formData.email, code: formData.verificationCode });
      goToStep('password');
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid or expired code');
    } finally {
      setLoading(false);
    }
  };
  
  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (!validatePassword(formData.password)) {
      setError('Password must contain 3 uppercase, 2 lowercase, 2 digits, and 1 special char (min 8 chars)');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    goToStep('details');
  };
  
  const handleDetailsSubmit = async (e) => {
    e.preventDefault();
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError('Please enter both first name and last name');
      return;
    }
    
    setLoading(true);
    try {
      await completeActivation({
        email: formData.email,
        code: formData.verificationCode,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName
      });
      setAlert({ type: 'success', message: 'Account activated successfully!' });
      navigate('/admins/signin');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to activate account');
    } finally {
      setLoading(false);
    }
  };
  
  const getStepTitle = () => {
    switch (currentStep) {
      case 'email': return 'Activate Admin Account';
      case 'verify': return 'Verify Your Email';
      case 'password': return 'Set Your Password';
      case 'details': return 'Complete Your Profile';
      default: return 'Activate Admin Account';
    }
  };
  
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'email':
        return (
          <form onSubmit={handleEmailSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-gray-700 mb-2">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email address"
                className="w-full p-3 
                border border-[#C1C7CD] 
                md:border-x-0 md:border-t-0 
                md:bg-[#F2F4F8] bg-transparent 
                rounded-md md:rounded-none 
                focus:outline-none 
                focus:border-[#174568] 
                md:focus:border-b-[#174568] md:focus:border-x-0 md:focus:border-t-0"
                required
                disabled={loading}
              />
              <p className="text-xs text-gray-600 mt-1">
                Enter the email address that was added to the admin database.
              </p>
            </div>
            
            {error && (
              <div className="text-red-600 text-sm">{error}</div>
            )}
            
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#174568] hover:bg-gray-800 disabled:bg-gray-400 text-white font-medium py-3 px-4 transition-colors"
            >
              {loading ? 'Checking...' : 'Send Verification Code'}
            </button>
          </form>
        );
        
      case 'verify':
        return (
          <form onSubmit={handleVerificationSubmit} className="space-y-4">
            <div className="text-center mb-4">
              <p className="text-green-600 text-sm">
                A 6-letter verification code has been sent to {formData.email}
              </p>
            </div>
            
            <div>
              <label htmlFor="verificationCode" className="block text-gray-700 mb-2">Verification Code</label>
              <input
                type="text"
                id="verificationCode"
                name="verificationCode"
                value={formData.verificationCode}
                onChange={(e) => {
                  const val = e.target.value.toUpperCase().replace(/[^A-Z]/g, '');
                  setFormData(prev => ({ ...prev, verificationCode: val }));
                  setError('');
                }}
                placeholder="ABCDEF"
                maxLength="6"
                className="w-full p-3 text-center text-2xl tracking-widest uppercase
                border border-[#C1C7CD] 
                md:border-x-0 md:border-t-0 
                md:bg-[#F2F4F8] bg-transparent 
                rounded-md md:rounded-none 
                focus:outline-none 
                focus:border-[#174568] 
                md:focus:border-b-[#174568] md:focus:border-x-0 md:focus:border-t-0"
                required
                disabled={loading}
              />
              <p className="text-center text-xs text-gray-500 mt-2">Enter the 6-letter verification code from your email</p>
            </div>
            
            {error && (
              <div className="text-red-600 text-sm">{error}</div>
            )}
            
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#174568] hover:bg-gray-800 disabled:bg-gray-400 text-white font-medium py-3 px-4 transition-colors"
            >
              {loading ? 'Verifying...' : 'Verify Code'}
            </button>
            
            <div className="text-center">
              <button
                type="button"
                onClick={() => goToStep('email')}
                className="text-[#001D6C] hover:underline text-sm"
              >
                Change email address
              </button>
            </div>
          </form>
        );
        
      case 'password':
        return (
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-gray-700 mb-2">New Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  className="w-full p-3 
                  border border-[#C1C7CD] 
                  md:border-x-0 md:border-t-0 
                  md:bg-[#F2F4F8] bg-transparent 
                  rounded-md md:rounded-none 
                  focus:outline-none 
                  focus:border-[#174568] 
                  md:focus:border-b-[#174568] md:focus:border-x-0 md:focus:border-t-0"
                  required
                />
                <button 
                  type="button"
                  onClick={() => togglePasswordVisibility('password')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            
            <div>
              <label htmlFor="confirmPassword" className="block text-gray-700 mb-2">Confirm Password</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm your password"
                  className="w-full p-3 
                  border border-[#C1C7CD] 
                  md:border-x-0 md:border-t-0 
                  md:bg-[#F2F4F8] bg-transparent 
                  rounded-md md:rounded-none 
                  focus:outline-none 
                  focus:border-[#174568] 
                  md:focus:border-b-[#174568] md:focus:border-x-0 md:focus:border-t-0"
                  required
                />
                <button 
                  type="button"
                  onClick={() => togglePasswordVisibility('confirm')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <p className="text-xs text-gray-600 mt-1">
               Password must be at least 8 characters long, contain at least 3 uppercase letters, 2 lowercase letters, 2 digits, and 1 special character.
              </p>
            </div>
            
            {error && (
              <div className="text-red-600 text-sm">{error}</div>
            )}
            
            <button
              type="submit"
              className="w-full bg-[#174568] hover:bg-gray-800 text-white font-medium py-3 px-4 transition-colors"
            >
              Set Password
            </button>
          </form>
        );
        
      case 'details':
        return (
          <form onSubmit={handleDetailsSubmit} className="space-y-4">
            <div>
              <label htmlFor="firstName" className="block text-gray-700 mb-2">First Name</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="Enter your first name"
                className="w-full p-3 
                border border-[#C1C7CD] 
                md:border-x-0 md:border-t-0 
                md:bg-[#F2F4F8] bg-transparent 
                rounded-md md:rounded-none 
                focus:outline-none 
                focus:border-[#174568] 
                md:focus:border-b-[#174568] md:focus:border-x-0 md:focus:border-t-0"
                required
                disabled={loading}
              />
            </div>
            
            <div>
              <label htmlFor="lastName" className="block text-gray-700 mb-2">Last Name</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Enter your last name"
                className="w-full p-3 
                border border-[#C1C7CD] 
                md:border-x-0 md:border-t-0 
                md:bg-[#F2F4F8] bg-transparent 
                rounded-md md:rounded-none 
                focus:outline-none 
                focus:border-[#174568] 
                md:focus:border-b-[#174568] md:focus:border-x-0 md:focus:border-t-0"
                required
                disabled={loading}
              />
            </div>
            
            {error && (
              <div className="text-red-600 text-sm">{error}</div>
            )}
            
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#174568] hover:bg-gray-800 disabled:bg-gray-400 text-white font-medium py-3 px-4 transition-colors"
            >
              {loading ? 'Activating Account...' : 'Complete Activation'}
            </button>
          </form>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="font-['Poppins'] bg-gray-50 min-h-screen flex md:flex-row-reverse flex-col">
      {/* Image section - full width on mobile, left half on desktop */}
      <div className="w-[88%] mt-[4rem] md:mt-0 mx-auto md:w-1/2 md:max-h-screen">
        {/* Close button - visible only on mobile */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 z-10 md:hidden"
          aria-label="Close"
        >
          <Link to={"/"}>
            <X size={24} className="text-gray-800" />
          </Link>
        </button>
        
        <img
          src={loginadmin}
          alt="Admin Activation"
          className="w-full h-64 md:h-full object-cover"
        />
      </div>
      
      {/* Form section - full width on mobile, right half on desktop */}
      <div className="md:w-1/2 px-6 py-8 md:px-16 md:py-12 md:flex md:flex-col md:justify-center">
        {/* Progress indicator */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className={`w-3 h-3 rounded-full ${currentStep === 'email' ? 'bg-[#174568]' : currentStep !== 'email' ? 'bg-green-500' : 'bg-gray-300'}`}></div>
            <div className={`flex-1 h-1 mx-2 ${currentStep === 'verify' || currentStep === 'password' || currentStep === 'details' ? 'bg-green-500' : 'bg-gray-300'}`}></div>
            <div className={`w-3 h-3 rounded-full ${currentStep === 'verify' ? 'bg-[#174568]' : currentStep === 'password' || currentStep === 'details' ? 'bg-green-500' : 'bg-gray-300'}`}></div>
            <div className={`flex-1 h-1 mx-2 ${currentStep === 'password' || currentStep === 'details' ? 'bg-green-500' : 'bg-gray-300'}`}></div>
            <div className={`w-3 h-3 rounded-full ${currentStep === 'password' ? 'bg-[#174568]' : currentStep === 'details' ? 'bg-green-500' : 'bg-gray-300'}`}></div>
            <div className={`flex-1 h-1 mx-2 ${currentStep === 'details' ? 'bg-green-500' : 'bg-gray-300'}`}></div>
            <div className={`w-3 h-3 rounded-full ${currentStep === 'details' ? 'bg-[#174568]' : 'bg-gray-300'}`}></div>
          </div>
          <div className="flex justify-between text-xs text-gray-600">
            <span>Email</span>
            <span>Verify</span>
            <span>Password</span>
            <span>Details</span>
          </div>
        </div>
        
        <h1 className="text-2xl font-bold text-[#174568] mb-6">{getStepTitle()}</h1>
        
        {renderCurrentStep()}
        
        <div className="text-left mt-6">
          <Link to={"/admins/signin"} className="text-[#001D6C] hover:underline">
            Already have an account? Sign in
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ActivateAdmin;